import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Wrench, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = `${window.__BACKEND_URL__}/api`;

// User Manual → Admin: every payroll ↔ QuickBooks status/error with cause → fix in HinesTime → verify.
// Content comes from GET /api/payroll/fix-guide (single source shared with error rows and Ask HinesTime).
export const PayrollSyncFixes = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const target = new URLSearchParams(window.location.search).get('fix') || (window.location.hash || '').replace('#fix-', '');
  useEffect(() => {
    axios.get(`${API}/payroll/fix-guide`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || 'Could not load the troubleshooting guide.'));
  }, [token]);
  useEffect(() => {
    if (!data || !target) return;
    const el = document.getElementById(`fix-${target}`);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); el.classList.add('ring-2', 'ring-sky-400'); }
  }, [data, target]);
  if (err) return <p className="text-sm text-red-300" data-testid="payroll-fixes-error">{err}</p>;
  if (!data) return <p className="text-sm text-slate-400" data-testid="payroll-fixes-loading">Loading troubleshooting guide…</p>;
  return (
    <div className="space-y-6" data-testid="payroll-sync-fixes">
      <div className="p-3 rounded-lg text-sm" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.3)' }} data-testid="payroll-fixes-intro">
        <p className="font-semibold text-white flex items-center gap-2"><Wrench className="w-4 h-4 text-sky-300" /> How payroll sync works — read once</p>
        <ul className="list-disc ml-5 mt-1 space-y-1 text-slate-300">
          <li>Hours flow <b>one way</b>: HinesTime → QuickBooks (one time activity per worker per day, Regular + OVERTIME). QuickBooks pays what it holds.</li>
          <li>Edits made inside QuickBooks by a payroll admin do <b>not</b> come back automatically. <b>Compare with QuickBooks</b> (push window) finds them — after every push automatically, or on demand — and lets you <b>Accept QuickBooks hours</b> (audited adjustment in HinesTime) or <b>Re-push HinesTime hours</b>.</li>
          <li>Always fix in HinesTime first (Edit session with a reason → Approve → push again). Days already sent are skipped; only fixed/failed days go.</li>
          <li>Every red row in the push window, readiness check and Compare has a <b>How to fix ↗</b> link that opens the matching section below. You can also ask <b>Ask HinesTime</b>: "why did my payroll push fail?"</li>
        </ul>
      </div>
      {data.groups.map((g) => (
        <div key={g} className="space-y-3" data-testid={`payroll-fixes-group-${g.replace(/\W+/g, '-').toLowerCase()}`}>
          <h4 className="text-base font-bold text-white border-b pb-1" style={{ borderColor: '#2D3748' }}>{g}</h4>
          {data.items.filter((i) => i.group === g).map((i) => (
            <div key={i.code} id={`fix-${i.code}`} className="rounded-lg p-3 scroll-mt-24" style={{ background: '#0F1923', border: '1px solid #2D3748' }} data-testid={`fix-${i.code}`}>
              <p className="text-sm font-semibold text-white">{i.title}</p>
              <p className="text-xs mt-1" style={{ color: '#94A3B8' }}><span className="font-semibold text-slate-300">What it means:</span> {i.means}</p>
              <ol className="list-decimal ml-5 mt-2 space-y-1 text-xs text-slate-200">
                {i.fix.map((s, n) => <li key={n}>{s}</li>)}
              </ol>
              <p className="text-xs mt-2 flex items-start gap-1" style={{ color: '#00C896' }}><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span><span className="font-semibold">You're done when:</span> {i.verify}</span></p>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
